/** IANA zone for India Standard Time (used for all merchant download filenames). */
const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Formats an instant as ddmmyyyy__hhmmss_mmm in IST (milliseconds optional).
 *
 * @param {string|number|Date} ts - Parsed by Date (e.g. API created_at or Date.now()).
 * @param {{ includeMilliseconds?: boolean }} [opts]
 * @returns {string|null}
 */
function formatInstantInIstForFilename(ts, opts = {}) {
  const { includeMilliseconds = false } = opts;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const val = (type) => parts.find((p) => p.type === type)?.value ?? '00';
  const y = val('year');
  const mo = val('month');
  const day = val('day');
  const h = val('hour');
  const min = val('minute');
  const s = val('second');
  // Keep a single canonical layout for every generated filename.
  let out = `${day}${mo}${y}__${h}${min}${s}`;
  if (includeMilliseconds) {
    const ms = String(d.getMilliseconds()).padStart(3, '0');
    out += `_${ms}`;
  }
  return out;
}

/**
 * Download basename for merchant AI generations: glammify_ddmmyyyy__hhmmss (IST from created_at).
 * Falls back to id if created_at is missing.
 *
 * @param {Object} generation - Generation row from API (dashboard, history, gallery, preview).
 * @returns {string} Basename without extension, safe for filesystems.
 */
export function getGenerationDownloadBaseName(generation) {
  const ts = generation?.created_at;
  if (ts) {
    const formatted = formatInstantInIstForFilename(ts);
    if (formatted) {
      return `glammify_${formatted}`;
    }
  }
  const id = generation?.id;
  if (id != null && id !== '') {
    return `glammify_${id}`;
  }
  return 'glammify_unknown';
}

/**
 * Full suggested download filename with extension.
 *
 * @param {Object} generation
 * @param {string} extension - e.g. 'jpg' or 'mp4' (leading dot stripped if present)
 * @returns {string}
 */
export function getGenerationDownloadFileName(generation, extension) {
  const ext = String(extension || 'jpg').replace(/^\./, '');
  return `${getGenerationDownloadBaseName(generation)}.${ext}`;
}

/**
 * One-shot names for gallery ZIP (current time in IST + ms suffix; same base for Flutter id and filename).
 *
 * @returns {{ baseName: string, fileName: string }} fileName ends with .zip
 */
export function createGalleryZipDownloadNames() {
  const formatted = formatInstantInIstForFilename(Date.now(), { includeMilliseconds: true });
  const body = formatted ? `glammify_${formatted}` : `glammify_fallback_${Date.now()}`;
  return { baseName: body, fileName: `${body}.zip` };
}

/**
 * One-shot filename for gallery PDF catalogue (current time in IST + ms suffix).
 *
 * @returns {string} filename ending with .pdf
 */
export function createGalleryPdfCatalogueFileName() {
  // Keep PDF naming aligned with generation/ZIP naming (glammify_ddmmyyyy__hhmmss_mmm.pdf).
  const formatted = formatInstantInIstForFilename(Date.now(), { includeMilliseconds: true });
  const body = formatted ? `glammify_${formatted}` : `glammify_fallback_${Date.now()}`;
  return `${body}.pdf`;
}
